import { createContext, useState, useEffect, useRef, useCallback } from "react";
import moment from "moment";
import { updateWellnessHelpful, fetchMyWellness } from "@/api/wellness.api";

import { timeHelper } from "../screens/StudentData/utils/timeHelper";
import { useDeepCompareMemo } from "use-deep-compare";
import { useAuth } from "@/features/auth";

export const MyStoryContext = createContext();

export const MyStoryProvider = ({ children }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [wellnessList, setWellnessList] = useState([]);
  const [filteredNextStepsData, setFilteredNextStepsData] = useState([]);
  const [bigFiveFilteredData, setBigFiveFilteredData] = useState([]);
  const [selectedTimes, setSelectedTimes] = useState({
    "All Day": true,
    "Early Morning": false,
    Morning: false,
    Afternoon: false,
    Evening: false,
  });

  const [helpfulFilter, setHelpfulFilter] = useState("All");
  const [selectedSubOptions, setSelectedSubOptions] = useState({});
  const [state, setState] = useState({
    favourites: false,
    sad: true,
    tired: true,
    lonely: true,
    happy: true,
    grateful: true,
    calm: true,
    stressed: true,
    scared: true,
    angry: true,
  });

  const [allData, setAllData] = useState(null);
  const [filterName, setFilterName] = useState("week");
  const [filterAmount, setFilterAmount] = useState(moment.duration(7, "days"));
  const [wellnessPopupOpen, setWellnessPopupOpen] = useState(false);
  const [whatHelpsData, setWhatHelpsData] = useState({});
  const [studentDashboardWhatHelps, setStudentDashboardWhatHelps] = useState(
    [],
  );
  const [selectedEmotion, setSelectedEmotion] = useState("all");
  const [selectedEmotionColor, setSelectedEmotionColor] = useState("#C8FADD");
  const [selectedTimeframe, setSelectedTimeframe] = useState("week");
  const [feeling, setFeeling] = useState("select");
  const [studentData, setStudentData] = useState([]);
  const [barGraphData, setBarGraphData] = useState([
    { name: "sad", count: 0, compare: 0 },
    { name: "tired", count: 0, compare: 0 },
    { name: "lonely", count: 0, compare: 0 },
    { name: "happy", count: 0, compare: 0 },
    { name: "grateful", count: 0, compare: 0 },
    { name: "calm", count: 0, compare: 0 },
    { name: "stressed", count: 0, compare: 0 },
    { name: "scared", count: 0, compare: 0 },
    { name: "angry", count: 0, compare: 0 },
  ]);
  const [tally, setTally] = useState(0);
  const [compareTally, setCompareTally] = useState(0);
  const [currentTabNumber, setCurrentTabNumber] = useState(0);
  const [districts, setDistricts] = useState({});
  const [chosenDistrict, setChosenDistrict] = useState("All Districts");
  const [chosenSchool, setChosenSchool] = useState("All Schools");
  const [chosenClass, setChosenClass] = useState("All Classes");
  const [chosenStudent, setChosenStudent] = useState("All Students");
  const [chosenEmotion, setChosenEmotion] = useState("all emotions");
  const [filterAmountCurrent, setFilterAmountCurrent] = useState(
    moment.duration(30, "days"),
  );
  const [filterAmountCompare, setFilterAmountCompare] = useState(
    moment.duration(),
  );
  const [studentEmailToNameMap, setStudentEmailToNameMap] = useState({});

  const timeframeToDayConversionDict = useDeepCompareMemo(
    () => ({
      day: 1,
      week: 7,
      month: 30,
      "3 months": 90,
      "6 months": 182,
      year: 365,
    }),
    [],
  );

  const changeBarGraphFilter = useCallback(
    (time) => {
      if (!studentData.length) return;

      const days = timeframeToDayConversionDict[time];
      const now = moment();
      const startCurrent = now.clone().subtract(days, "days");
      const startCompare = now.clone().subtract(days * 2, "days");

      // Init emotion counters
      const currentEmotions = {
        sad: 0,
        tired: 0,
        lonely: 0,
        happy: 0,
        grateful: 0,
        calm: 0,
        stressed: 0,
        scared: 0,
        angry: 0,
      };
      const compareEmotions = { ...currentEmotions };

      studentData.forEach((datapoint) => {
        const timestamp = new Date(datapoint.date).getTime();
        const dateMoment = moment(timestamp);

        if (dateMoment.isBetween(startCurrent, now, null, "[)")) {
          currentEmotions[datapoint.emotion]++;
        } else if (
          dateMoment.isBetween(startCompare, startCurrent, null, "[)")
        ) {
          compareEmotions[datapoint.emotion]++;
        }
      });

      const newBarGraphData = Object.keys(currentEmotions).map((emotion) => ({
        name: emotion,
        count: currentEmotions[emotion],
        compare: compareEmotions[emotion],
      }));

      setBarGraphData(newBarGraphData);
      setTally(calculateTotal(currentEmotions));
      setCompareTally(calculateTotal(compareEmotions));
    },
    [studentData, timeframeToDayConversionDict],
  );

  const fetchAllData = useCallback(async () => {
    if (!user) return;

    setTally(0);
    setCompareTally(0);
    setLoading(true);
    try {
      const resp = await fetchMyWellness();
      setAllData(resp);
      setStudentData(resp);
      changeBarGraphFilter(filterName);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, [user, changeBarGraphFilter, filterName]);

  const calculateTotal = (dict) => {
    return Object.values(dict).reduce((acc, value) => acc + value, 0);
  };

  const gatherWellnessPractices = (feeling, time) => {
    setFilterName(time);
    let filter = moment.duration(timeframeToDayConversionDict[time], "days");

    let emotionDict = {
      sad: {},
      tired: {},
      lonely: {},
      happy: {},
      grateful: {},
      calm: {},
      stressed: {},
      scared: {},
      angry: {},
    };

    studentData.forEach((datapoint) => {
      if (
        new Date(datapoint.date).getTime() >=
        moment().subtract(filter).valueOf()
      ) {
        let feeling = datapoint.emotion;
        let helped = datapoint.helpful;
        let action = datapoint.action;

        if (helped) {
          if (!(action in emotionDict[feeling])) {
            emotionDict[feeling][action] = 0;
          }
          emotionDict[feeling][action]++;
        }
      }
    });

    setFeeling(feeling);
    if (feeling === "select") {
      setWellnessList([]);
    } else {
      let feelingList = Object.keys(emotionDict[feeling]);
      if (feelingList.length > 3) {
        let objectList = Object.keys(emotionDict[feeling]).sort(
          (a, b) => emotionDict[feeling][b] - emotionDict[feeling][a],
        );
        setWellnessList(objectList.slice(0, 3));
      } else {
        setWellnessList(feelingList);
      }
    }

    setBigFiveFilteredData(
      studentData.filter(
        (data) =>
          new Date(data.date).getTime() >= moment().subtract(filter).valueOf(),
      ),
    );
  };

  const helpful = async (data) => {
    try {
      await updateWellnessHelpful(data._id, true);

      const updatedData = studentData.map((item) =>
        item._id === data._id ? { ...item, helpful: !data.helpful } : item,
      );
      setStudentData(updatedData);
      setAllData(updatedData);
    } catch (error) {}
  };

  // Prevent duplicate fetches on mount/StrictMode and only refetch when user id changes
  const hasLoadedRef = useRef(false);
  const lastUserIdRef = useRef(null);

  useEffect(() => {
    const userId =
      user && user._id && user._id.toString ? user._id.toString() : user?._id;

    const shouldLoad =
      !!userId && (!hasLoadedRef.current || lastUserIdRef.current !== userId);
    if (shouldLoad) {
      lastUserIdRef.current = userId;
      hasLoadedRef.current = true;
      fetchAllData();
    }
  }, [user]);

  useEffect(() => {
    if (!allData) return;
    try {
      const whatHelps =
        (allData || [])
          .filter(
            (data) =>
              (data.emotion === selectedEmotion || selectedEmotion === "all") &&
              moment(data.date).isAfter(
                moment().subtract(timeHelper(selectedTimeframe)),
              ) &&
              data.helpful === true,
          )
          .map((item) => ({
            ...item,
            hourfield: new Date(item.date).getUTCHours(),
          })) || [];
      setWhatHelpsData(whatHelps);

      const studentDashboardWhatHelps =
        (allData || [])
          .filter((data) =>
            // (data.emotion === selectedEmotion || selectedEmotion === "all") &&
            moment(data.date).isAfter(
              moment().subtract(timeHelper(filterName)),
            ),
          )
          .map((item) => ({
            ...item,
            hourfield: new Date(item.date).getUTCHours(),
          })) || [];
      setStudentDashboardWhatHelps(studentDashboardWhatHelps);

      setBigFiveFilteredData(allData || []);
    } catch (error) {
      setWhatHelpsData([]);
      setStudentDashboardWhatHelps([]);
      setBigFiveFilteredData([]);
    }
  }, [
    selectedEmotion,
    selectedEmotionColor,
    selectedTimeframe,
    filterName,
    allData,
  ]);

  useEffect(() => {
    changeBarGraphFilter(filterName);
  }, [wellnessList, filterName, studentData]);

  const value = {
    loading,
    wellnessList,
    filteredNextStepsData,
    bigFiveFilteredData,
    setBigFiveFilteredData,
    selectedTimes,
    setSelectedTimes,
    helpfulFilter,
    setHelpfulFilter,
    selectedSubOptions,
    setSelectedSubOptions,
    state,
    setState,
    allData,
    filterName,
    setFilterName,
    filterAmount,
    setFilterAmount,
    wellnessPopupOpen,
    setWellnessPopupOpen,
    whatHelpsData,
    studentDashboardWhatHelps,
    selectedEmotion,
    setSelectedEmotion,
    selectedEmotionColor,
    setSelectedEmotionColor,
    selectedTimeframe,
    setSelectedTimeframe,
    feeling,
    setFeeling,
    studentData,
    barGraphData,
    tally,
    compareTally,
    currentTabNumber,
    setCurrentTabNumber,
    fetchAllData,
    changeBarGraphFilter,
    gatherWellnessPractices,
    helpful,
    districts,
    chosenDistrict,
    chosenSchool,
    chosenClass,
    chosenStudent,
    chosenEmotion,
    filterAmountCurrent,
    filterAmountCompare,
    studentEmailToNameMap,
  };

  const myStoryContext = useDeepCompareMemo(() => value, [value]);

  return (
    <MyStoryContext.Provider value={myStoryContext}>
      {children}
    </MyStoryContext.Provider>
  );
};
